/**
 * `startProperties.tracking` — the marketing site's ad-attribution capture (ad-network click-ids +
 * `utm_*` + first-touch metadata), e.g.
 *
 *   { "gclid": "EAIaIQ4x9", "utm_source": "google", "utm_medium": "cpc",
 *     "trackingid": "wlm8k2p_a1f3", "_first_ts": 1752566400, "_first_landing": "/", "_via": "lp" }
 *
 * This module is deliberately APP-LEVEL (not inside a configurator flow, not under `nova/`) so BOTH
 * the configurator flows' `lib/startProperties.ts` (which decodes the full envelope) AND the
 * app-wide persistence hook (`nova/theme/usePersistStartTracking.ts`, mounted in `OwsTheme` — the
 * only provider wrapping every route) can share ONE canonical validator without an import inversion.
 * It mirrors the self-contained decode in `src/utils/start-properties-market.ts` /
 * `src/utils/configurator-flow.ts` (same reason: avoid importing a flow's internals).
 *
 * Validation follows `docs/app-startproperties-tracking.md` §5–6. `tracking` is UNTRUSTED,
 * attacker-controllable input (the whole envelope rides in a user-editable URL), so every field is
 * re-validated here at the boundary; a broken / hostile hand-off yields `null` or a dropped field,
 * never a thrown error.
 *
 * Read DURABLE-first: the boot shim (`src/dev.js`) copies the raw `tracking` object into its own
 * `sessionStorage` key ({@link START_TRACKING_KEY}) so the hand-off survives the auth-gate `/login`
 * redirect (which drops the query string) AND the one-shot raw-`startProperties` stash being
 * consumed by the configurator's locale hand-off. The raw param (URL or its stash) is decoded here
 * only as a fallback.
 */

/** Query-param carrying the base64-encoded hand-off (mirrors startProperties). */
const START_PROPERTIES_PARAM = 'startProperties';
/** sessionStorage key the boot shim stashes the RAW `startProperties` param under. */
const START_PROPERTIES_STASH_KEY = 'ows-start-properties';
/** Durable sessionStorage key the boot shim (`src/dev.js`) copies the raw `tracking` object into.
 *  Literal MIRRORS `src/dev.js` — keep both in sync. */
export const START_TRACKING_KEY = 'ows-start-tracking';
/** Durable sessionStorage key the boot shim pre-extracts the TOP-LEVEL `flow` under (the onboarding
 *  variant, e.g. `v0` / `v3`). Literal MIRRORS `src/dev.js` + `src/utils/configurator-flow.ts`. */
const START_FLOW_KEY = 'ows-start-flow';

/** Max accepted raw-`startProperties` length before decoding — spec §5.3 recommends ~16 KB. A cheap
 *  DoS guard: reject a giant (hostile) value rather than decode it. Mirrored in `src/dev.js` and
 *  each flow's `lib/startProperties.ts` (`MAX_RAW_LENGTH`). */
const MAX_RAW_LENGTH = 16 * 1024;

/**
 * A sanitized `tracking` snapshot: a flat, SPARSE bag of the allowlisted ad-network params +
 * internal join ids (`trackingid`, `cpid`, `cid`, `flow`) and up to 4 first-touch metadata fields
 * (`_first_ts` / `_last_ts` numbers, `_first_landing` path, `_via` enum). Only keys actually present
 * on the visitor's URL are included, PLUS the onboarding `flow` variant folded in from the top-level
 * `startProperties` (see {@link readStartTracking}). Analytics/attribution data only — NEVER an
 * authorization / pricing / entitlement input.
 */
export type StartTracking = Record<string, string | number>;

/* ---- tracking validation — mirrors `docs/app-startproperties-tracking.md` §5–6 ----
 *
 * FIELD-level policy: a malformed value drops that one field and keeps the rest (a weird `utm_term`
 * must never cost a good `gclid`); only a non-object `tracking` is rejected wholesale. Unknown keys
 * are ACCEPTED but bounded as PARAM-class strings — forward-compat, since the site's allowlist grows
 * as new ad networks are added and we don't want to silently drop a new param until the app ships an
 * update.
 */
/** Object-level cap on stored keys (allowlist is ~66 params + 4 meta; bounds a param-pollution payload). */
const TRACKING_MAX_KEYS = 80;
/** Key-name allowlist: bounded alphanumeric + underscore (accepts the `_`-prefixed meta keys). */
const TRACKING_KEY_RE = /^[A-Za-z0-9_]{1,64}$/;
/** Object-prototype key names refused outright — `JSON.parse` materialises a real own `__proto__`
 *  property, so skip these defensively (our string/number values can't poison a prototype, but this
 *  removes any doubt and keeps the snapshot a plain data bag). */
const TRACKING_UNSAFE_KEYS: ReadonlySet<string> = new Set(['__proto__', 'prototype', 'constructor']);
/** ID class (`cpid` / `cid` / `flow`): strict id charset — injection-safe lookup keys. */
const TRACKING_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** TRACKINGID class (`trackingid`): id charset plus `.` `:` (the `/go/` + client-minted id formats). */
const TRACKING_TRACKINGID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
/** PARAM class max length — caps storage / DoS abuse while allowing real ad values (spaces, unicode). */
const TRACKING_PARAM_MAX = 512;
/** PATH class (`_first_landing`) max length. */
const TRACKING_PATH_MAX = 2048;
/** TIMESTAMP class sane range: 2020-09-13 .. 2100-01-01 (matches the §6 JSON-schema bounds). A forged
 *  far-past / far-future ts can't corrupt attribution-window logic; treat as advisory regardless. */
const TRACKING_TS_MIN = 1_600_000_000;
const TRACKING_TS_MAX = 4_102_444_800;
/** The internal ID-class keys (validated with the strict id charset, not as free PARAM strings). */
const TRACKING_ID_KEYS: ReadonlySet<string> = new Set(['cpid', 'cid', 'flow']);

/**
 * Strip C0 control chars (U+0000–U+001F) + DEL (U+007F): blocks CRLF log-injection and NUL tricks
 * (spec §5.2 PARAM). Iterates code points (astral chars preserved) so it needs no control-char
 * regex literal.
 */
const stripControlChars = (v: string): string => {
  let out = '';
  for (const ch of v) {
    const code = ch.charCodeAt(0);
    if (code > 0x1f && code !== 0x7f) out += ch;
  }
  return out;
};

/**
 * Sanitize ONE tracking value by its key's class (§5.2). Returns the cleaned value, or `undefined`
 * to DROP the field. The 4 meta keys are typed (integer timestamps, an enum, a relative path); the
 * internal ids get the strict id charset; everything else (all ad-network params + unknown
 * forward-compat keys) is a PARAM-class string: control-stripped, trimmed, length-capped.
 */
const sanitizeTrackingValue = (key: string, value: unknown): string | number | undefined => {
  // TIMESTAMP class — an integer in a sane epoch-seconds range.
  if (key === '_first_ts' || key === '_last_ts') {
    return typeof value === 'number' && Number.isInteger(value) && value >= TRACKING_TS_MIN && value <= TRACKING_TS_MAX
      ? value
      : undefined;
  }
  // ENUM class — a closed set; anything else pollutes analytics, so drop it.
  if (key === '_via') return value === 'lp' || value === 'direct' ? value : undefined;
  // Every remaining class is a string.
  if (typeof value !== 'string') return undefined;
  // PATH class — a relative path only (no scheme / protocol-relative / backslash / control chars), so
  // it can't be mistaken for an absolute URL (open-redirect / injection) when reported or displayed.
  if (key === '_first_landing') {
    const v = value.trim();
    if (!v || v.length > TRACKING_PATH_MAX) return undefined;
    if (!v.startsWith('/') || v.startsWith('//')) return undefined;
    if (v.includes(':') || v.includes('\\') || stripControlChars(v) !== v) return undefined;
    return v;
  }
  // ID class — strict internal-id charset.
  if (TRACKING_ID_KEYS.has(key)) {
    const v = value.trim();
    return TRACKING_ID_RE.test(v) ? v : undefined;
  }
  // TRACKINGID class — id charset plus `.` `:`.
  if (key === 'trackingid') {
    const v = value.trim();
    return TRACKING_TRACKINGID_RE.test(v) ? v : undefined;
  }
  // PARAM class (all ad-network params + unknown forward-compat keys).
  const v = stripControlChars(value).trim();
  return v && v.length <= TRACKING_PARAM_MAX ? v : undefined;
};

/**
 * Narrow + sanitize an unknown decoded value to a {@link StartTracking}, or `undefined` when it
 * carries nothing usable. Rejects a non-object wholesale (§5.3); otherwise walks the keys, dropping
 * any that fail the key-name allowlist or whose value fails its class rule, and stops once
 * {@link TRACKING_MAX_KEYS} have been kept. Returns `undefined` for `{}` / all-dropped so a
 * tracking-less hand-off never creates an empty pref.
 */
export function parseTracking(value: unknown): StartTracking | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const src = value as Record<string, unknown>;
  const out: StartTracking = {};
  let count = 0;
  for (const key of Object.keys(src)) {
    if (count >= TRACKING_MAX_KEYS) break;
    if (!TRACKING_KEY_RE.test(key) || TRACKING_UNSAFE_KEYS.has(key)) continue;
    const clean = sanitizeTrackingValue(key, src[key]);
    if (clean !== undefined) {
      out[key] = clean;
      count += 1;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * UTF-8-safe base64 decode tolerant of URL-safe base64 + missing padding (a `+` in a query string is
 * decoded to a space by `URLSearchParams`). MIRRORS the decode in `start-properties-market.ts`,
 * `configurator-flow.ts`, and each flow's `lib/startProperties.ts`.
 */
function decodeBase64Utf8(raw: string): string {
  let b64 = raw.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Raw `startProperties` value from the URL, else the boot-time sessionStorage stash. */
function readRawStartProperties(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(START_PROPERTIES_PARAM);
    if (fromUrl) return fromUrl;
  } catch {
    /* URL unparseable — fall through to the stash. */
  }
  return safeSessionGet(START_PROPERTIES_STASH_KEY);
}

/** Validate + normalise the onboarding flow variant — ID class (spec §5.2), same charset the
 *  `tracking` object's own `flow` key gets. */
const cleanFlow = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return TRACKING_ID_RE.test(s) ? s : null;
};

/**
 * The onboarding flow variant (`v0` / `v3`) from the TOP-LEVEL `startProperties` — the durable
 * `ows-start-flow` key first (set at boot, survives the auth gate + raw-stash consume), then the raw
 * param. Spec §3: the top-level `flow` is AUTHORITATIVE for this field, so it's read here rather than
 * trusting a `flow` that merely rode inside the `tracking` object.
 */
function readStartFlow(): string | null {
  const fromKey = cleanFlow(safeSessionGet(START_FLOW_KEY));
  if (fromKey) return fromKey;
  const raw = readRawStartProperties();
  if (!raw || raw.length > MAX_RAW_LENGTH) return null;
  try {
    const decoded: unknown = JSON.parse(decodeBase64Utf8(raw));
    if (!decoded || typeof decoded !== 'object') return null;
    return cleanFlow((decoded as Record<string, unknown>).flow);
  } catch {
    return null;
  }
}

/** Sanitized `tracking` from the durable key (the boot shim's raw copy of the `tracking` object). */
function fromDurableKey(): StartTracking | null {
  const raw = safeSessionGet(START_TRACKING_KEY);
  if (!raw || raw.length > MAX_RAW_LENGTH) return null;
  try {
    return parseTracking(JSON.parse(raw)) ?? null;
  } catch {
    return null;
  }
}

/** Sanitized `tracking` decoded from the raw `startProperties` param (URL or its stash) — the
 *  fallback when the durable key is absent (e.g. sessionStorage was unavailable at boot). */
function fromRawParam(): StartTracking | null {
  const raw = readRawStartProperties();
  if (!raw || raw.length > MAX_RAW_LENGTH) return null;
  try {
    const decoded: unknown = JSON.parse(decodeBase64Utf8(raw));
    if (!decoded || typeof decoded !== 'object') return null;
    return parseTracking((decoded as Record<string, unknown>).tracking) ?? null;
  } catch {
    return null;
  }
}

/**
 * The sanitized ad-attribution snapshot the marketing site handed off this session, or `null` when
 * none was sent. Durable-key first (survives the auth gate, register, session mint, reloads, and the
 * raw-stash consume), then the raw param as a fallback. Pure + side-effect-free; never throws.
 *
 * The TOP-LEVEL `flow` (onboarding variant, e.g. `v0` / `v3`) is folded in as an authoritative
 * `flow` field (spec §3) — overriding any `flow` that rode inside the `tracking` object — so the
 * persisted snapshot records how the account started.
 *
 * A `flow` ALONE never yields a snapshot: without real tracking there is nothing to attach it to,
 * and a stray / left-over `ows-start-flow` (an organic visit, or a value lingering in sessionStorage
 * across a session) would otherwise write a bogus `{ flow }`-only pref that, under immutable
 * first-touch, permanently blocks the real ad-attribution from ever being saved.
 */
export function readStartTracking(): StartTracking | null {
  const tracking = fromDurableKey() ?? fromRawParam();
  if (!tracking) return null;
  const flow = readStartFlow();
  return flow ? { ...tracking, flow } : tracking;
}

/** Consume the durable `tracking` hand-off once it has been persisted, so a later account (shared
 *  browser) doesn't inherit it. Best-effort — a failed clear just means a redundant (idempotent,
 *  immutable-guarded) re-persist attempt on the next authenticated load. */
export function clearStartTracking(): void {
  try {
    sessionStorage.removeItem(START_TRACKING_KEY);
  } catch {
    /* nothing to clear. */
  }
}
